import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from 'hooks/useAuth';
import { traiComplianceService, A2pCategory } from 'api/services/traiCompliance.service';
import { CATEGORY_ADMINS, hasRole } from 'pages/Compliance/traiRules';
import { CallCategorySelect } from './CallCategoryField';

/**
 * The organisation's default TRAI call category, for the Settings page. Only rendered behind the
 * trai-compliance build flag. Unset = today's behaviour (calls are unclassified).
 */
const OrgCategoryCard: React.FC = () => {
  const { currentOrganization, user } = useAuth();
  const canEdit = hasRole((currentOrganization?.role || user?.role)?.toLowerCase(), CATEGORY_ADMINS);
  const [value, setValue] = useState<'' | A2pCategory>('');
  const [saved, setSaved] = useState<'' | A2pCategory>('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    traiComplianceService.categories().then((r) => {
      if (r.success && r.data) {
        const v = (r.data.orgDefault || '') as '' | A2pCategory;
        setValue(v); setSaved(v);
      }
    });
  }, []);

  const save = async () => {
    setBusy(true); setNote(null);
    const r = await traiComplianceService.setOrgCategory(value || null);
    setBusy(false);
    if (r.success) { setSaved(value); setNote('Saved'); } else setNote(r.error || 'Could not save');
  };

  return (
    <div className="tl-card p-5 mb-4 lg:mb-6 space-y-2">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Default call category (TRAI)</h2>
      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
        What this organisation's automated calls are for when a campaign, agent or request does not say.
        Recorded and reported only. More in <Link className="underline" to="/compliance/categories">Compliance (TRAI)</Link>.
      </p>
      <div className="flex items-center gap-2">
        <CallCategorySelect value={value} onChange={setValue} className="tl-input" />
        {canEdit && (
          <button className="tl-btn tl-btn-primary tl-btn-sm disabled:opacity-50" disabled={busy || value === saved} onClick={save}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
          </button>
        )}
        {note && <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{note}</span>}
      </div>
    </div>
  );
};

export default OrgCategoryCard;
